import { useState } from 'react'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { InlineAlert } from '../../../components/InlineAlert'
import { Icon } from '../../../components/icons/Icon'
import { ApiError } from '../../../api/problem'
import { generateCartridgeDraft } from '../../../api/aiCartridgeDraft'
import type {
  AiCartridgeDraftProposalView,
  AiDraftKind,
  AiProvenanceSourceInput,
} from '../../../types/aiCartridgeDraft'

const DRAFT_KINDS: { value: AiDraftKind; label: string }[] = [
  { value: 'rule_set', label: 'RuleSet (rate/threshold structure)' },
  { value: 'parameter', label: 'Parameter (a rate/threshold value)' },
  { value: 'eligibility_model', label: 'EligibilityModel (a decision table)' },
]

const SOURCE_TYPES = ['statutory_text', 'guidance_document'] as const

interface CitationRow extends AiProvenanceSourceInput {
  key: number
}

let nextRowKey = 1

export interface AiDraftRequestDialogProps {
  programId: string
  onClose: () => void
  onGenerated: (proposal: AiCartridgeDraftProposalView) => void
}

/**
 * `POST /api/v1/cartridge-drafts` (IB-286, FR-M7.5) request form — mirrors `CloneProgramDialog`'s
 * shape (`FocusTrapDialog`, `InlineAlert` for inline errors, `iw-field`/`iw-input`/`iw-btn` tokens,
 * a `friendly*Error` mapper keyed off `ApiError`).
 *
 * <b>[IB-286-verified] contract correction.</b> The DoR/design assumed a free-text "guidance/statute
 * text" box the author pastes into. The merged backend accepts no such field — `groundingSources` is
 * a list of structured `{sourceType, sourceId, role}` citations (the same shape `module.ai`'s
 * `AiProvenance` already uses platform-wide), and `inputsSnapshotRef` is a plain reference string
 * folded into the composer's deterministic output, not raw statute content. This form is built to
 * that real contract: an "Inputs snapshot reference" text field plus a small repeatable citation list
 * (source type, a source id, and which rule/gate it grounds) — never an invented free-text box that
 * would silently be dropped on submit.
 */
export function AiDraftRequestDialog({ programId, onClose, onGenerated }: AiDraftRequestDialogProps) {
  const [draftKind, setDraftKind] = useState<AiDraftKind>('rule_set')
  const [inputsSnapshotRef, setInputsSnapshotRef] = useState('')
  const [citations, setCitations] = useState<CitationRow[]>([
    { key: nextRowKey++, sourceType: 'statutory_text', sourceId: '', role: '' },
  ])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function updateCitation(key: number, patch: Partial<AiProvenanceSourceInput>) {
    setCitations((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)))
  }

  function addCitation() {
    setCitations((prev) => [...prev, { key: nextRowKey++, sourceType: 'statutory_text', sourceId: '', role: '' }])
  }

  function removeCitation(key: number) {
    setCitations((prev) => prev.filter((c) => c.key !== key))
  }

  async function handleSubmit() {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const groundingSources = citations
        .filter((c) => c.sourceId.trim() !== '')
        .map((c) => ({ sourceType: c.sourceType, sourceId: c.sourceId.trim(), role: c.role.trim() }))
      const proposal = await generateCartridgeDraft({
        draftKind,
        programId,
        inputsSnapshotRef: inputsSnapshotRef.trim() || null,
        groundingSources,
      })
      onGenerated(proposal)
    } catch (err) {
      setError(friendlyGenerateError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog titleId="ai-draft-request-title" title="Request an AI draft" onClose={onClose} width={560}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Requesting…' : 'Request AI draft'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        <InlineAlert severity="info">
          AI-assisted draft, produced by a deterministic template today (not a live model). It proposes a
          draft only — you still review, and it still travels the full draft → review → approve → publish
          path before it is canon.
        </InlineAlert>

        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="ai-draft-kind">
            Target artifact
          </label>
          <select
            id="ai-draft-kind"
            className="iw-input"
            value={draftKind}
            onChange={(e) => setDraftKind(e.target.value as AiDraftKind)}
          >
            {DRAFT_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="ai-draft-snapshot-ref">
            Inputs snapshot reference (optional)
          </label>
          <input
            id="ai-draft-snapshot-ref"
            className="iw-input"
            value={inputsSnapshotRef}
            onChange={(e) => setInputsSnapshotRef(e.target.value)}
            placeholder="e.g. a document/version reference this run is grounded in"
          />
        </div>

        <div className="iw-field">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
            <span className="iw-field-label">Grounding citations (optional)</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="iw-btn iw-btn--sm" onClick={addCitation}>
              <Icon name="plus" size={12} /> Add citation
            </button>
          </div>
          {citations.map((c) => (
            <div key={c.key} style={{ display: 'flex', gap: 'var(--iw-space-2)', marginTop: 'var(--iw-space-2)' }}>
              <select
                aria-label="Citation source type"
                className="iw-input"
                value={c.sourceType}
                onChange={(e) => updateCitation(c.key, { sourceType: e.target.value })}
                style={{ maxWidth: 180 }}
              >
                {SOURCE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.replace('_', ' ')}
                  </option>
                ))}
              </select>
              <input
                aria-label="Citation source id"
                className="iw-input"
                value={c.sourceId}
                onChange={(e) => updateCitation(c.key, { sourceId: e.target.value })}
                placeholder="statute/guidance document id"
              />
              <input
                aria-label="Which rule or gate this citation grounds"
                className="iw-input"
                value={c.role}
                onChange={(e) => updateCitation(c.key, { role: e.target.value })}
                placeholder="which rule/gate it grounds"
              />
              <button
                type="button"
                className="iw-icon-btn"
                aria-label="Remove citation"
                onClick={() => removeCitation(c.key)}
              >
                <Icon name="x" size={14} />
              </button>
            </div>
          ))}
        </div>
      </form>
    </FocusTrapDialog>
  )
}

/** Every failure mode named in the design doc §5: 403 (capability denial), 404 (Program, or "no
 * RuleSet yet" for a Parameter draft), 422 (unrecognized draftKind / unmapped source_type). */
function friendlyGenerateError(err: unknown): string {
  if (err instanceof ApiError) {
    const slug = err.problem.type?.split('/').pop()
    const detail = err.problem.detail
    if (err.status === 403 || slug === 'capability-denied') {
      return detail
        ? `AI-assisted drafting is a platform-only capability. ${detail}`
        : 'AI-assisted drafting is a platform-only capability — your current session cannot perform it.'
    }
    if (err.status === 404 || slug === 'not-found') {
      return detail ?? 'This Program (or a RuleSet for it to attach a Parameter draft to) could not be found.'
    }
    if (err.status === 422) {
      return detail ?? 'This request could not be processed — check the target artifact and grounding citations.'
    }
    return detail ?? err.message
  }
  return 'Could not request an AI draft. Please try again.'
}
